import { useState, useEffect } from "react";
import type { Category, CategoryAttributeDefinition } from "@/app/api/auth-client";
import { getCategories } from "@/app/api/auth-client";
import { attributeDefsForCategory } from "@/app/components/category-listing-attributes";

function humanizeAttrKey(key: string): string {
  if (!key.trim()) return key;
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatListingAttributeValue(def: CategoryAttributeDefinition | undefined, v: unknown): string {
  if (v === null || v === undefined) return "";
  if (v === "") return "";
  const t = (def?.type || "").toLowerCase();
  if (t === "boolean" || typeof v === "boolean") {
    if (typeof v === "boolean") return v ? "Sim" : "Não";
    const s = String(v).toLowerCase();
    return s === "true" ? "Sim" : "Não";
  }
  if (t === "number" || typeof v === "number") {
    const n = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
    if (!Number.isNaN(n) && Number.isFinite(n)) {
      return Number.isInteger(n) ? String(n) : n.toLocaleString("pt-BR", { maximumFractionDigits: 2 });
    }
  }
  return String(v);
}

function isEmptyValue(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === "string" && v.trim() === "") return true;
  return false;
}

type Row = { key: string; label: string; value: string };

function buildRows(
  attrs: Record<string, unknown>,
  defs: CategoryAttributeDefinition[]
): Row[] {
  const used = new Set<string>();
  const rows: Row[] = [];

  for (const d of defs) {
    if (!Object.prototype.hasOwnProperty.call(attrs, d.key)) continue;
    const v = attrs[d.key];
    if (isEmptyValue(v)) continue;
    const value = formatListingAttributeValue(d, v);
    if (!value) continue;
    rows.push({
      key: d.key,
      label: (d.label_pt || "").trim() || humanizeAttrKey(d.key),
      value,
    });
    used.add(d.key);
  }

  for (const key of Object.keys(attrs)) {
    if (used.has(key)) continue;
    const v = attrs[key];
    if (isEmptyValue(v)) continue;
    const value = formatListingAttributeValue(undefined, v);
    if (!value) continue;
    rows.push({
      key,
      label: humanizeAttrKey(key),
      value,
    });
  }

  return rows;
}

interface ListingAttributesDisplayProps {
  categoryId?: string;
  listingAttributes?: Record<string, unknown>;
}

/**
 * Read-only listing attributes for ad detail (below description).
 * Loads category schema for labels; falls back to humanized keys if catalog fails or key is unknown.
 */
export function ListingAttributesDisplay({
  categoryId,
  listingAttributes,
}: ListingAttributesDisplayProps) {
  const [categories, setCategories] = useState<Category[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCategories()
      .then((list) => {
        if (!cancelled) setCategories(list);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const attrs = listingAttributes;
  if (!attrs || Object.keys(attrs).length === 0) {
    return null;
  }

  if (categories === null) {
    return null;
  }

  const defs = attributeDefsForCategory(categories, categoryId ?? "");
  const rows = buildRows(attrs, defs);
  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="border-t border-border pt-6">
      <h3 className="mb-3 text-lg font-medium">Detalhes do produto</h3>
      <dl className="space-y-3">
        {rows.map((row) => (
          <div
            key={row.key}
            className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-4"
          >
            <dt className="text-sm font-medium text-foreground sm:w-[40%] sm:min-w-[8rem] shrink-0">
              {row.label}
            </dt>
            <dd className="text-sm text-muted-foreground sm:flex-1">{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
